import { SESSION_REPLAY_LIMITS } from "@backchannel/tax-contracts";

const SESSION_KEY = "tax_session_replay_id";
const ACTIVITY_KEY = "tax_session_replay_activity";
const SEQUENCE_PREFIX = "tax_session_replay_seq:";
const ACTIVITY_WRITE_THROTTLE_MS = 30_000;

type RotateListener = (nextId: string, previousId: string) => void;

type MemoryStore = {
  sessionId: string | null;
  lastActivityAt: number;
  sequences: Map<string, number>;
};

const memory: MemoryStore = {
  sessionId: null,
  lastActivityAt: 0,
  sequences: new Map(),
};

const rotateListeners = new Set<RotateListener>();

let activityListenersInstalled = false;
let idleRotationMs = SESSION_REPLAY_LIMITS.idleRotationMs;
let lastActivityWriteAt = 0;

function storage(): Storage | null {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null;
  } catch {
    return null;
  }
}

function readStored(key: string): string | null {
  try {
    return storage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    storage()?.setItem(key, value);
  } catch {
    /* memory fallback only */
  }
}

function removeStored(key: string): void {
  try {
    storage()?.removeItem(key);
  } catch {
    /* ignore */
  }
}

function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `00000000-0000-4000-8000-${Date.now().toString(16).padStart(12, "0")}`;
}

function readActivityAt(): number {
  const raw = readStored(ACTIVITY_KEY);
  if (raw) {
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return memory.lastActivityAt;
}

function writeActivityAt(at: number): void {
  memory.lastActivityAt = at;
  writeStored(ACTIVITY_KEY, String(at));
}

function readSessionId(): string | null {
  return readStored(SESSION_KEY) ?? memory.sessionId;
}

function writeSessionId(id: string): void {
  memory.sessionId = id;
  writeStored(SESSION_KEY, id);
}

function sequenceKey(id: string): string {
  return `${SEQUENCE_PREFIX}${id}`;
}

function readSequence(id: string): number {
  const raw = readStored(sequenceKey(id));
  if (raw) {
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed >= 0) return Math.floor(parsed);
  }
  return memory.sequences.get(id) ?? 0;
}

function writeSequence(id: string, sequence: number): void {
  memory.sequences.set(id, sequence);
  writeStored(sequenceKey(id), String(sequence));
}

function createSession(now = Date.now()): string {
  const id = newId();
  writeSessionId(id);
  writeActivityAt(now);
  writeSequence(id, 0);
  lastActivityWriteAt = now;
  return id;
}

function rotateIfIdle(now = Date.now()): string {
  const current = readSessionId();
  const lastActivity = readActivityAt();
  if (current && lastActivity > 0 && now - lastActivity >= idleRotationMs) {
    const previous = current;
    removeStored(sequenceKey(previous));
    memory.sequences.delete(previous);
    const next = createSession(now);
    for (const listener of rotateListeners) {
      try {
        listener(next, previous);
      } catch {
        /* never throw from rotation */
      }
    }
    return next;
  }
  if (!current) return createSession(now);
  return current;
}

function noteActivity(now = Date.now()): void {
  // Ensure an id exists before recording activity.
  rotateIfIdle(now);
  if (now - lastActivityWriteAt < ACTIVITY_WRITE_THROTTLE_MS && readActivityAt() > 0) {
    memory.lastActivityAt = now;
    return;
  }
  writeActivityAt(now);
  lastActivityWriteAt = now;
}

function onVisibility(): void {
  if (document.visibilityState === "visible") noteActivity();
}

function installActivityListeners(): void {
  if (activityListenersInstalled || typeof window === "undefined") return;
  activityListenersInstalled = true;
  window.addEventListener("pointerdown", () => noteActivity(), { passive: true });
  window.addEventListener("keydown", () => noteActivity(), { passive: true });
  document.addEventListener("visibilitychange", onVisibility);
}

/**
 * Configure idle rotation (from the server config) and ensure activity
 * listeners are installed. Safe to call more than once.
 */
export function configureReplaySession(options?: { idleRotationMs?: number }): void {
  if (options?.idleRotationMs && options.idleRotationMs > 0) {
    idleRotationMs = options.idleRotationMs;
  }
  installActivityListeners();
}

/**
 * Current tab-scoped replay session id. Creates one when missing; never
 * rotates as a side effect of this call (use {@link touchReplaySession} /
 * activity listeners for rotation).
 */
export function currentReplaySessionId(): string {
  installActivityListeners();
  const existing = readSessionId();
  if (existing) return existing;
  return createSession();
}

/** Mark activity and rotate when idle; returns the id after any rotation. */
export function touchReplaySession(): string {
  installActivityListeners();
  return rotateIfIdle();
}

/** Next chunk sequence for the current session id (persisted, then incremented). */
export function nextReplaySequence(replaySessionId = currentReplaySessionId()): number {
  const current = readSequence(replaySessionId);
  writeSequence(replaySessionId, current + 1);
  return current;
}

/** Starts a new session now (without notifying listeners: the caller seals its own chunk). */
export function rotateReplaySession(): string {
  const previous = readSessionId();
  if (previous) {
    removeStored(sequenceKey(previous));
    memory.sequences.delete(previous);
  }
  return createSession();
}

export function onRotate(listener: RotateListener): () => void {
  rotateListeners.add(listener);
  return () => {
    rotateListeners.delete(listener);
  };
}

/** Test helper: clear memory + sessionStorage keys. */
export function resetReplaySessionForTests(): void {
  memory.sessionId = null;
  memory.lastActivityAt = 0;
  memory.sequences.clear();
  lastActivityWriteAt = 0;
  idleRotationMs = SESSION_REPLAY_LIMITS.idleRotationMs;
  removeStored(SESSION_KEY);
  removeStored(ACTIVITY_KEY);
}
